import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Plus, Search, Play, Clock, Trash2, Film } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  Draft: "status-draft",
  StoryboardReady: "status-storyboardready",
  ClipsQA: "status-clipsqa",
  Rendering: "status-rendering",
  Delivered: "status-delivered",
  Failed: "status-failed",
};

const STATUS_LABELS = {
  Draft: "Draft",
  StoryboardReady: "Storyboard Ready",
  ClipsQA: "Clips QA",
  Rendering: "Rendering",
  Delivered: "Delivered",
  Failed: "Failed",
};

const FILTERS = ["all", "Draft", "StoryboardReady", "ClipsQA", "Rendering", "Delivered"];

export default function Dashboard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    try {
      const res = await api.get("/sessions", { params: { status: filter } });
      setSessions(res.data);
    } catch (err) {
      toast.error("Failed to load sessions");
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    try {
      const res = await api.post("/sessions", { property_label: newLabel, property_details_text: newDesc });
      toast.success("Session created");
      setShowCreate(false);
      setNewLabel("");
      setNewDesc("");
      navigate(`/sessions/${res.data.id}`);
    } catch (err) {
      toast.error("Failed to create session");
    }
    setCreating(false);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      toast.success("Session deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = sessions.filter(s =>
    s.property_label?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Dashboard</h1>
          <p className="text-[#676970] mt-1">Manage your property video sessions</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          data-testid="new-session-btn"
          className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white font-medium h-10 px-5 rounded-lg"
        >
          <Plus size={16} className="mr-2" />
          New Session
        </Button>
      </div>

      {/* Filters + Search */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-2">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${
                filter === f
                  ? "bg-[#4D11F6] text-white"
                  : "bg-white text-[#676970] border border-[#E2E4E7] hover:border-[#4D11F6]/30"
              }`}
            >
              {f === "all" ? "All" : STATUS_LABELS[f] || f}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs ml-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94969C]" />
          <Input
            placeholder="Search sessions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="session-search-input"
            className="pl-9 h-9 bg-white border-[#E2E4E7] text-sm"
          />
        </div>
      </div>

      {/* Sessions Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-[#E2E4E7] p-5">
              <Skeleton className="h-4 w-3/4 mb-3" />
              <Skeleton className="h-3 w-1/2 mb-4" />
              <Skeleton className="h-8 w-20" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
          <div className="w-16 h-16 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mb-5">
            <Film size={28} className="text-[#4D11F6]" strokeWidth={1.5} />
          </div>
          <h3 className="text-xl font-semibold text-[#020B23] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
            {search ? "No matching sessions" : "No sessions yet"}
          </h3>
          <p className="text-[#676970] mb-6 max-w-sm">
            {search ? "Try a different search term" : "Create your first session to start generating property walkthrough videos."}
          </p>
          {!search && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              <Plus size={16} className="mr-2" /> Create First Session
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((session, idx) => (
            <div
              key={session.id}
              onClick={() => navigate(`/sessions/${session.id}`)}
              data-testid={`session-card-${session.id}`}
              className="bg-white rounded-xl border border-[#E2E4E7] p-5 cursor-pointer hover:shadow-lg hover:border-[#4D11F6]/30 transition-all duration-200 animate-fade-in group"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-[#020B23] text-base truncate pr-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {session.property_label}
                </h3>
                <button
                  onClick={(e) => handleDelete(session.id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-[#94969C] hover:text-[#F5354E] transition-all"
                  data-testid={`delete-session-${session.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              {session.property_details_text && (
                <p className="text-[#676970] text-sm mb-4 line-clamp-2">
                  {session.property_details_text}
                </p>
              )}
              <div className="flex items-center justify-between">
                <Badge variant="outline" className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${STATUS_STYLES[session.status] || "status-draft"}`}>
                  {STATUS_LABELS[session.status] || session.status}
                </Badge>
                <span className="flex items-center gap-1.5 text-xs text-[#94969C]">
                  <Clock size={12} />
                  {new Date(session.updated_at).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-4 pt-3 border-t border-[#F2F3F4]">
                <span className="text-sm font-medium text-[#4D11F6] flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                  <Play size={14} /> Resume
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Session Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="create-session-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>
              New Session
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Property Label</Label>
              <Input
                placeholder="e.g., 42 Sunset Blvd, Malibu"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                data-testid="new-session-label-input"
                className="h-10 bg-white border-[#E2E4E7]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Property Description (optional)</Label>
              <Textarea
                placeholder="Describe the property features, location, price..."
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                data-testid="new-session-desc-input"
                rows={3}
                className="bg-white border-[#E2E4E7] resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#E2E4E7] text-[#676970]">Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !newLabel.trim()} data-testid="create-session-submit-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
              {creating ? "Creating..." : "Create Session"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
